import styles from "./Modals.module.scss";
import React, { useCallback, useRef, useState } from "react";
import type { Destination, IncidentTypeValue } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { IncidentTypesSelect } from "../components/IncidentTypesSelect";
import { useDestinationTestConnectionSubmit } from "../utils/useDestinationTestConnectionSubmit";

const BASE_DATA: Destination = {
  type: "sentinelDlpIncident" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS,
  settings: {
    tenantId: "",
    clientId: "",
    clientSecret: "",
    dceEndpoint: "",
    dcrImmutableId: "",
    streamName: "",
  },
};

type FormDataType = FormDestinationDataType<"sentinelDlpIncident"> & {
  incidentTypes?: IncidentTypeValue[];
};

export const SentinelModalForm: React.FC<DestinationFormProps<"sentinelDlpIncident">> = ({ settings, destinationType, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);

  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.tenantId) {
      errors.tenantId = t("field_is_required");
    }
    if (!values.clientId) {
      errors.clientId = t("field_is_required");
    }
    if (!values.clientSecret) {
      errors.clientSecret = t("field_is_required");
    }
    if (!values.dceEndpoint) {
      errors.dceEndpoint = t("field_is_required");
    }
    if (!values.dcrImmutableId) {
      errors.dcrImmutableId = t("field_is_required");
    }
    if (!values.incidentTypes?.length) {
      errors.incidentTypes = t("field_is_required");
    }
    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = useCallback(() => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS,
      type: destinationType,
      settings: { ...data, streamName: data.streamName || undefined },
    };
  }, [destinationType]);

  const onSubmit = useDestinationTestConnectionSubmit({ getFormValues, validate, onSubmitForm, store, t });

  return (
    <>
      <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        <IncidentTypesSelect name="incidentTypes" />
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.tenant_id")} required />
          <FormTextInput name={"tenantId"} paddingInline="none" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.client_id")} required />
          <FormTextInput name={"clientId"} paddingInline="none" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.client_secret")} required />
          <FormPasswordTextInput name={"clientSecret"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.dce_endpoint")} required />
          <FormTextInput name={"dceEndpoint"} paddingInline="none" placeholder="https://dce-xxx.region.ingest.monitor.azure.com" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.dcr_immutable_id")} required />
          <FormTextInput name={"dcrImmutableId"} paddingInline="none" placeholder="dcr-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.sentinel.stream_name")} />
          <FormTextInput name={"streamName"} paddingInline="none" placeholder="Custom-TuvisIncidents_CL" />
        </div>
      </FormElement>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </>
  );
};
